// @ts-nocheck

import {
  Activity,
  BadgePercent,
  BarChart3,
  Bell,
  Bot,
  Book,
  BookAudio,
  Building2,
  Calendar,
  Coins,
  Contact,
  Cog,
  Database,
  FileBarChart,
  FileText,
  Globe,
  Headphones,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Megaphone,
  MessageSquare,
  Network,
  PhoneCall,
  PhoneOutgoing,
  RadioTower,
  Radio,
  Server,
  Shield,
  Shuffle,
  StickyNote,
  Users,
  UserPlus,
  Video,
  Voicemail,
  Wallet,
  Webhook,
  Workflow,
  Wrench,
  ChartArea,
  Music,
  type LucideIcon,
} from 'lucide-react';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';

const { SUPERADMIN, SUB_SUPERADMIN, RESELLER, COMPANY_ADMIN, CALL_CENTRE_ADMIN, CALL_CENTRE_TEAM_LEAD, CALL_CENTRE_AGENT } = USER_ROLES;

// Historical name kept for the diff — v2 originally used NON_AGENT to
// blanket-hide items from the CALL_CENTRE_AGENT role. That is not what
// v1 does: v1's verticle-nav-with-searchbar.js gates almost every item
// with `canView(module)` — a pure ACL check — and never role-blocks
// agents from ACL-granted items. If a customer's agent has view_permission
// for a module, they see it in the sidebar (v1 behavior confirmed with
// EUCUCCA1 which shows Contacts / Reports / Disposition Forms / Activity
// List). So NON_AGENT is now equivalent to ALL_ROLES; ACL gates handle
// the actual filtering. Only real hard role-blocks (e.g. Users hidden
// from CALL_CENTRE_ADMIN, or SUPERADMIN-only items) stay narrow.
const NON_AGENT = [SUPERADMIN, SUB_SUPERADMIN, RESELLER, COMPANY_ADMIN, CALL_CENTRE_ADMIN, CALL_CENTRE_TEAM_LEAD, CALL_CENTRE_AGENT];
const NON_AGENT_NON_CCADMIN = [SUPERADMIN, SUB_SUPERADMIN, RESELLER, COMPANY_ADMIN, CALL_CENTRE_TEAM_LEAD, CALL_CENTRE_AGENT];
const ADMIN = [SUPERADMIN, SUB_SUPERADMIN];
const SA_AND_CCADMIN = [SUPERADMIN, CALL_CENTRE_ADMIN];
const TENANT_ONLY = [SUPERADMIN, SUB_SUPERADMIN, RESELLER, COMPANY_ADMIN];
const ALL_ROLES = NON_AGENT;
// True agent-exclusion set — matches v1's explicit `userRole !== CALL_CENTRE_AGENT`
// hard-gates on Agent Leads (verticle-nav-with-searchbar.js:558) and
// Session Replay (line 754). Even if an agent's ACL grants the module,
// v1 hides these two items. Use only for those two.
const EXCLUDE_AGENT = [SUPERADMIN, SUB_SUPERADMIN, RESELLER, COMPANY_ADMIN, CALL_CENTRE_ADMIN, CALL_CENTRE_TEAM_LEAD];

export interface NavLeaf {
  label: string;
  to: string;
  icon?: LucideIcon;
  roles: string[];
  aclModule?: string;
  /**
   * Roles that bypass this item's aclModule check.
   * Mirrors v1's `isSuperAdmin || canView(mod)` pattern for items like
   * MOH Management, Flash Message, Session Replay, Notifications.
   */
  bypassAclForRoles?: string[];
  hideForOwnCompany?: boolean;
  requireAclOps?: string[];
}
export interface NavGroup {
  label: string;
  icon: LucideIcon;
  roles: string[];
  aclModule?: string;
  bypassAclForRoles?: string[];
  hideForOwnCompany?: boolean;
  children: NavLeaf[];
}
export type NavItem = NavLeaf | NavGroup;
export function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

/*
 * Item order matches v1 verticle-nav-with-searchbar.js exactly.
 * Grouped by v1's `parent` field where present; leaves otherwise.
 */
export const NAV_ITEMS: NavItem[] = [
  // 1. Dashboard
  { label: 'Dashboard', icon: LayoutDashboard, to: '/' + ROUTES.DASHBOARD, roles: ALL_ROLES },
  { label: 'CTI Preview', icon: Headphones, to: '/cti-preview', roles: [CALL_CENTRE_AGENT] },
  { label: 'Agent CTI', icon: Headphones, to: '/cti-signal', roles: [CALL_CENTRE_AGENT] },
  { label: 'Agent Console', icon: Headphones, to: '/cti2', roles: [CALL_CENTRE_TEAM_LEAD] },

  // 2. Users — tenant identity screens, hidden from every call-centre-scoped
  // role (admin, team lead, agent). Routes are blocked too, see DenyRoles.
  { label: 'Users', icon: Users, to: '/' + ROUTES.USERS.LIST, roles: TENANT_ONLY, aclModule: 'Users' },

  // 3. Companies
  //    Display label reads "Companies"; the ACL module name stays
  //    "Company List" — that's the identifier the server ACL system
  //    keys off, changing it would break permission checks.
  { label: 'Companies', icon: Building2, to: '/' + ROUTES.COMPANIES.LIST, roles: NON_AGENT, aclModule: 'Company List' },

  // 4. Licence History — v1 gate: `isSuperAdmin` (line 59)
  { label: 'Licence History', icon: History, to: '/' + ROUTES.LICENCE_HISTORY.LIST, roles: [SUPERADMIN] },

  // 5. Country Codes — v1 gate: `isSuperAdmin` (line 68)
  { label: 'Country Codes', icon: Globe, to: '/' + ROUTES.COUNTRY_CODE_DIRECTIONS.LIST, roles: [SUPERADMIN] },

  // 6. Release Notes — v1 gate: `isSuperAdmin` (line 76)
  { label: 'Release Notes', icon: Book, to: '/' + ROUTES.RELEASE_CENTER.LIST, roles: [SUPERADMIN] },

  // 7. Trunks
  { label: 'Trunks', icon: Network, to: '/' + ROUTES.TRUNKS.LIST, roles: NON_AGENT, aclModule: 'Trunks' },

  // 8. Campaigns (group)
  {
    label: 'Campaigns',
    icon: Megaphone,
    roles: NON_AGENT,
    aclModule: 'Campaigns',
    children: [
      { label: 'Live Campaigns', to: '/' + ROUTES.CAMPAIGNS.LIST, roles: NON_AGENT },
      { label: 'Campaigns Summary', to: '/' + ROUTES.CAMPAIGNS.SUMMARY, roles: NON_AGENT },
    ],
  },

  // 9. Call Scripts
  { label: 'Call Scripts', icon: FileText, to: '/' + ROUTES.CALL_SCRIPT.LIST, roles: NON_AGENT, aclModule: 'Call Script' },

  // 10. Contacts + Contact Group — flattened from a group with a single
  // relevant child. Both live at the top level now.
  { label: 'Contacts',      icon: Contact, to: '/' + ROUTES.CONTACTGROUP.ALL_CONTACTS, roles: NON_AGENT, aclModule: 'Contacts' },
  { label: 'Contact Groups', icon: Contact, to: '/' + ROUTES.CONTACTGROUP.LIST,        roles: NON_AGENT, aclModule: 'Contact Group' },

  // 11. Breaks
  { label: 'Breaks', icon: Calendar, to: '/' + ROUTES.BREAKS.LIST, roles: NON_AGENT, aclModule: 'Breaks' },

  // 12. Credits (group)
  {
    label: 'Credits',
    icon: Coins,
    roles: NON_AGENT,
    aclModule: 'Credit History',
    children: [
      { label: 'History', to: '/' + ROUTES.CREDIT_HISTORY, roles: NON_AGENT },
      { label: 'TTS Credits Logs', to: '/' + ROUTES.TTS_CREDITS_LOGS, roles: NON_AGENT },
      { label: 'Voice Credits Logs', to: '/' + ROUTES.VOICE_CREDITS_LOGS, roles: NON_AGENT, hideForOwnCompany: true },
      { label: 'AI Agent Voice Credits Logs', to: '/' + ROUTES.AI_AGENT_CREDITS_LOGS, roles: NON_AGENT },
      { label: 'Summary', to: '/' + ROUTES.CREDIT_SUMMARY, roles: NON_AGENT },
    ],
  },

  // 13. Numbers (group) — sub-items 2-4 are v1 `isSuperAdmin` only (line 175)
  {
    label: 'Numbers',
    icon: RadioTower,
    roles: NON_AGENT,
    aclModule: 'Numbers',
    children: [
      { label: 'Numbers', to: '/' + ROUTES.NUMBERS.LIST, roles: NON_AGENT },
      { label: 'Numbers Files List', to: '/' + ROUTES.NUMBERS.NUMBER_FILE_LIST, roles: [SUPERADMIN] },
      { label: 'Status for approval', to: '/' + ROUTES.NUMBERS.STATUS_CHANGE_ACTION, roles: [SUPERADMIN] },
      { label: 'Historical DIDs', to: '/' + ROUTES.NUMBERS.DID_HISTORY, roles: [SUPERADMIN] },
    ],
  },

  // 14. Demo Account Settings — v1 gate: `isSuperAdmin` (line 197)
  { label: 'Demo Account Settings', icon: BadgePercent, to: '/' + ROUTES.DEMO_ACCOUNT_SETTINGS.LIST, roles: [SUPERADMIN] },

  // 15. Call Centers (leaves) — ACL modules ('Call Centres List') keep
  // the original spelling since the backend keys off that literal.
  { label: 'Call Centers', icon: Headphones, to: '/' + ROUTES.CALL_CENTRES.LIST, roles: NON_AGENT, aclModule: 'Call Centres List' },

  // 15b. AI Agent (group) — module 
  {
    label: 'AI Agent',
    icon: Bot,
    roles: NON_AGENT,
    aclModule: MODULE_ACL.AI_AGENT,
    children: [
      { label: 'Knowledge Base', to: '/' + ROUTES.AI_AGENT.KNOWLEDGE_BASE_LIST, roles: NON_AGENT, aclModule: MODULE_ACL.AI_AGENT },
      { label: 'AI Agent Builder', to: '/' + ROUTES.AI_AGENT.AGENTS, roles: NON_AGENT, aclModule: MODULE_ACL.AI_AGENT },
    ],
  },

  // 16. IVR (group)
  {
    label: 'IVR',
    icon: PhoneCall,
    roles: NON_AGENT,
    children: [
      { label: 'IVR List', to: '/' + ROUTES.IVR.LIST.replace(':cid', '0'), roles: NON_AGENT, aclModule: 'IVR' },
      { label: 'Voice Files', to: '/' + ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES, roles: NON_AGENT, aclModule: 'Voice Library' },
      { label: 'TTS Voice Profile', to: '/' + ROUTES.TTS.VOICE_PROFILE_LIST, roles: NON_AGENT, aclModule: 'TTS' },
      { label: 'TTS Add', to: '/' + ROUTES.TTS.CREATE, roles: NON_AGENT, aclModule: 'TTS' },
      { label: 'TTS List', to: '/' + ROUTES.TTS.LIST, roles: NON_AGENT, aclModule: 'TTS' },
    ],
  },

  // 17. Dialer Groups — unified page (Groups / Assign Cos / Remove Cos tabs)
  {
    label: 'Dialer Groups',
    icon: Users,
    to: '/' + ROUTES.MANAGE_GROUPS.GROUP_LIST,
    roles: ADMIN,
    aclModule: 'Dialer Groups',
  },

  // 18. Dialer Locations — unified page (Locations / Assign Cos / Remove Cos tabs)
  {
    label: 'Dialer Locations',
    icon: MapPin,
    to: '/' + ROUTES.MANAGE_LOCATIONS.LOCATION_LIST,
    roles: ADMIN,
    aclModule: 'Dialer Locations',
  },

  // 19. PBX Management
  { label: 'PBX', icon: Server, to: '/' + ROUTES.MANAGE_DIALERS.DIALER_LISTING, roles: ADMIN, aclModule: 'PBX Management' },

  // 20. Reports (group)
  {
    label: 'Reports',
    icon: FileBarChart,
    roles: NON_AGENT,
    children: [
      { label: 'Incoming Summary', to: '/' + ROUTES.IVR.INCOMING_SUMMARY, roles: NON_AGENT, aclModule: 'Incoming Summary' },
      { label: 'Outgoing Summary', to: '/' + ROUTES.IVR.OUTCOMING_SUMMARY, roles: NON_AGENT, aclModule: 'Outgoing Summary' },
      { label: 'CDR Report', to: '/' + ROUTES.IVR.CDR_REPORT, roles: NON_AGENT, aclModule: 'CDR Report' },
      {
        label: 'Agent Performance Report',
        to: '/' + ROUTES.IVR.AGENT_PERFORMANCE_REPORT,
        roles: [CALL_CENTRE_ADMIN],
      },
      // v1 uses the singular ACL module name "Download Report" (verticle-nav
      // -with-searchbar.js:445). Match verbatim so backend grants like
      // moduleOperations["Download Report"].view_permission resolve.
      { label: 'Download Reports', to: '/' + ROUTES.IVR.DOWNLOAD_REPORT, roles: NON_AGENT, aclModule: 'Download Report' },
    ],
  },

  { label: 'Operators', icon: RadioTower, to: '/' + ROUTES.OPERATORS.LIST, roles: [SUPERADMIN] },

  // 21. Data Center
  { label: 'Data Center', icon: Database, to: '/' + ROUTES.DATA_CENTER.LIST, roles: ADMIN, aclModule: 'Data Center' },

  // 22. Webhooks (group) — v1 had these as three flat leaves; grouped here
  // under one parent for a tidier sidebar. Each child keeps its own exact
  // label / route / roles / aclModule from before — nothing else changed.
  {
    label: 'Manage Webhooks',
    icon: Webhook,
    roles: NON_AGENT,
    children: [
      { label: 'Webhook', to: '/' + ROUTES.MANAGE_WEBHOOK.LIST, roles: NON_AGENT, aclModule: 'Webhook' },
      { label: 'SMS Webhook', to: '/' + ROUTES.SMSWEBHOOK.LIST, roles: NON_AGENT, aclModule: 'SMS Webhook' },
      { label: 'API Webhook', to: '/' + ROUTES.APIWEBHOOK.LIST, roles: NON_AGENT, aclModule: 'API Webhook' },
    ],
  },

  // 23. Voice (SUPERADMIN only in v1)
  { label: 'Voice', icon: BookAudio, to: '/' + ROUTES.VOICE_LIBRARY.MANAGE_PROMPTS, roles: [SUPERADMIN] },

  // 24. MOH Management — v1: `isSuperAdmin || canView("MOH Management")` (line 399)
  { label: 'MOH', icon: Music, to: '/' + ROUTES.VOICE_LIBRARY.MOH_MANAGEMENT, roles: NON_AGENT, aclModule: 'MOH Management', bypassAclForRoles: [SUPERADMIN] },

  // 25. Gateway Management
  { label: 'Gateways', icon: Wrench, to: '/' + ROUTES.SERVER_MANAGEMENT.LIST, roles: ADMIN, aclModule: 'Gateway Management' },

  // 26. Modules (group)
  //     ACL Setting List is gated by ACL alone for everyone but agents, so a
  //     reseller / company admin holding the ACL sees it (v1 uses canView()
  //     with no role check). Module List stays admin-only.
  {
    label: 'Modules',
    icon: Shield,
    roles: EXCLUDE_AGENT,
    children: [
      { label: 'Module List', to: '/' + ROUTES.MODULE.LIST, roles: ADMIN, aclModule: 'Module List' },
      { label: 'ACL Setting List', to: '/' + ROUTES.ACL_SETTING.LIST, roles: EXCLUDE_AGENT, aclModule: 'ACL Setting List' },
      { label: 'ACL History', to: '/' + ROUTES.ACL_SETTING.HISTORY, roles: [SUPERADMIN], aclModule: 'ACL Setting List', bypassAclForRoles: [SUPERADMIN] },
      { label: 'ACL Lock Audit', to: '/' + ROUTES.ACL_SETTING.LOCK_AUDIT, roles: [SUPERADMIN], aclModule: 'ACL Setting List', bypassAclForRoles: [SUPERADMIN] },
    ],
  },

  // 27. Agents (group)
  {
    label: 'Agents',
    icon: Headphones,
    roles: NON_AGENT,
    children: [
      { label: 'Agents', to: '/' + ROUTES.MANAGE_AGENTS.AGENTS, roles: NON_AGENT, aclModule: 'Agents' },
      { label: 'Call Group', to: '/' + ROUTES.MANAGE_AGENTS.AGENT_GROUP_LIST, roles: NON_AGENT, aclModule: 'Call Group' },
      { label: 'Call Group Analytics', to: '/' + ROUTES.MANAGE_AGENTS.AGENT_GROUP_ANALYTICS, roles: NON_AGENT, aclModule: 'Call Group' },
      // Agents keep their leads UI in CTI. Everyone else sees this item only
      // when their ACL grants the Agent Leads module.
      { label: 'Agent Leads', to: '/' + ROUTES.AGENT_LEADS.ADMIN_LIST, roles: EXCLUDE_AGENT, aclModule: 'Agent Leads' },
      { label: 'Agent Announcement', to: '/' + ROUTES.AGENT_ANNOUNCEMENT.LIST, roles: [SUPERADMIN] },
      { label: 'Agent Idle Stats', to: '/' + ROUTES.AGENT_IDLE_STATS.LIST, roles: [SUPERADMIN] },
      { label: 'Agent Activity', to: '/' + ROUTES.AGENT_ACTIVITY_TRACKING, roles: [SUPERADMIN] },
      // v1: `!ccagent && (isSuperAdmin || canView("Session Replay"))` (line 754)
      { label: 'Session Replay', to: '/' + ROUTES.SESSION_REPLAY.LIST, roles: EXCLUDE_AGENT, aclModule: 'Session Replay', bypassAclForRoles: [SUPERADMIN] },
    ],
  },

  // 28. Extension
  { label: 'Extension', icon: Radio, to: '/' + ROUTES.MANAGE_AGENTS.EXTENSION, roles: NON_AGENT, aclModule: 'Extension' },

  // 29. Agent File List (SUPERADMIN only)
  { label: 'Agent File List', icon: FileText, to: '/' + ROUTES.MANAGE_AGENTS.AGENT_FILE_LIST, roles: [SUPERADMIN] },

  // 30. Activity List
  { label: 'Activity List', icon: Activity, to: '/' + ROUTES.JOURNEY_OPERATION.LIST, roles: NON_AGENT, aclModule: 'Activity List' },

  // 31. Voice Plans
  { label: 'Voice Plans', icon: Wallet, to: '/' + ROUTES.VOICE_PLAN.LIST, roles: NON_AGENT, aclModule: 'Voice Plans' },

  // 32. Flash Message — v1: `canView("Flash Message") || SUPERADMIN` (line 505)
  // { label: 'Flash Message', icon: MessageSquare, to: '/' + ROUTES.FLASH_MESSAGE.LIST, roles: NON_AGENT, aclModule: 'Flash Message', bypassAclForRoles: [SUPERADMIN] },

  // 33. TTS Plans
  { label: 'TTS Plans', icon: Wallet, to: '/' + ROUTES.TTSPLANS.LIST, roles: NON_AGENT, aclModule: 'TTS Plans' },

  // 34. Realtime
  { label: 'Realtime', icon: Video, to: '/' + ROUTES.REALTIME.LIST, roles: NON_AGENT, aclModule: 'Realtime' },
  // 34b. Live Calls — Redis live-call mirror read through /live-calls; same ACL module as Realtime.
  { label: 'Live Calls', icon: Radio, to: '/' + ROUTES.REALTIME.LIVE_CALLS, roles: NON_AGENT, aclModule: 'Realtime', bypassAclForRoles: [SUPERADMIN] },

  // 35. Remarks
  { label: 'Remarks', icon: StickyNote, to: '/' + ROUTES.REMARKS.LIST, roles: NON_AGENT, aclModule: 'Remarks Status' },

  // 36. Logs (SUPERADMIN only)
  { label: 'Logs', icon: FileText, to: '/' + ROUTES.BACKEND_LOGS, roles: [SUPERADMIN] },

  // 37. API Metrics (SUPERADMIN only)
  { label: 'API Metrics', icon: ChartArea, to: '/' + ROUTES.API_METRICS, roles: [SUPERADMIN] },

  // 38. Disposition Forms
  { label: 'Disposition Forms', icon: ListChecks, to: '/' + ROUTES.DISPOSITION_FORMS.LIST, roles: NON_AGENT, aclModule: 'Disposition Forms' },

  // Pages that had a route but no sidebar entry, so search could not find them.
  { label: 'Disposition Follow-ups', icon: ListChecks, to: '/' + ROUTES.DISPOSITION_FOLLOW_UPS.LIST, roles: SA_AND_CCADMIN, aclModule: 'Disposition Forms', requireAclOps: ['read'] },
  { label: 'Voicemails', icon: Voicemail, to: '/' + ROUTES.VOICEMAILS.LIST, roles: NON_AGENT, aclModule: MODULE_ACL.VOICEMAIL },

  // 39. Number Operations (SUPERADMIN + SUB_SUPERADMIN)
  {
    label: 'Number Operations',
    icon: Shuffle,
    roles: ADMIN,
    children: [
      { label: 'Manage Blacklist-DND', to: '/' + ROUTES.NUMBER_OPERATIONS.MANAGE_BLACKLIST_DND, roles: ADMIN },
      { label: 'View Blacklist-DND', to: '/' + ROUTES.NUMBER_OPERATIONS.VIEW_BLACKLIST_DND, roles: ADMIN },
      { label: 'Search DND-Blist', to: '/' + ROUTES.NUMBER_OPERATIONS.SEARCH_DND_BLIST, roles: ADMIN },
    ],
  },

  // 40. Voice Blacklist (COMPANY_ADMIN only)
  {
    label: 'Voice Blacklist',
    icon: PhoneOutgoing,
    to: '/' + ROUTES.VOICE_BLACKLIST.VIEW,
    roles: [COMPANY_ADMIN],
  },

  // 41. Notifications — v1: `isNotificationAdmin || canView("Notification")`
  // where isNotificationAdmin = SUPERADMIN || SUB_SUPERADMIN (line 632)
  {
    label: 'Notifications',
    icon: Bell,
    roles: NON_AGENT,
    aclModule: 'Notification',
    bypassAclForRoles: [SUPERADMIN, SUB_SUPERADMIN],
    children: [
      { label: 'List', to: '/' + ROUTES.NOTIFICATION.LIST, roles: NON_AGENT, aclModule: 'Notification', bypassAclForRoles: [SUPERADMIN, SUB_SUPERADMIN] },
      { label: 'Assigned', to: '/' + ROUTES.NOTIFICATION.ASSIGNMENTS, roles: NON_AGENT, aclModule: 'Notification', bypassAclForRoles: [SUPERADMIN, SUB_SUPERADMIN] },
    ],
  },

  // 42. WebRTC Call Quality (main-dev feature — nested under Reports in v1
  // it's actually a route not in v1 sidebar, but we surface it for admin visibility.)
  // Route lives at "/call-quality" (root-level child, not nested under /dashboard).
  { label: 'Call Quality', icon: BarChart3, to: '/' + ROUTES.CALL_QUALITY.LIST, roles: ADMIN },
];
